import { ExtendedEmailShare, SocialHandle } from '@components/share-social-media-buttons/ShareSocialMediaButtons';
import { Author, Category, Image, Link, Pagination, Sizes } from '@customTypes/index';
import { api, shapeBlockData, shapeRelatedPostsData } from '@services/Api';
import moment from 'moment';
import queryString from 'query-string';
import { decode } from "html-entities";

export interface Attrs {
  id?: number;
  issueId?: number;
  sizes: Sizes;
  link: Link;
  title: string;
  ctaType: string;
  playlistId: string;
  dateFormat: string;
  orderBy?: any;
  perRow?: number;
  total?: number;
  order?: number;
  flipImg?: string;
  showFields?: [];
  image: Image;
  linkDestination?: any;
}

export interface SocialShare {
  email: ExtendedEmailShare;
  article: { isEnabled: boolean; url: string; };
  facebook: SocialHandle;
  instagram: SocialHandle;
  twitter: SocialHandle
};

export interface Block {
  blockName: string;
  html: string;
  jumpId: string;
  attrs: Attrs;
}

export interface Taxonomy {
  category: Category[];
  tags: Category[];
}

export interface SimplePagination {
  previous: Link;
  next: Link;
}

export interface GoogleAd {
  clientId: string;
  slotId: string;
}

export interface Result {
  ID: number;
  title: string;
  slug: string;
  date: string;
  link: Link;
  description?: any;
  blocks?: Block[];
  content?: string;
  authors?: Author[];
  ads?: GoogleAd[];
  socialShare?: SocialShare;
  type?: string;
  isNewPost?: boolean;
  showElements?: string[];
  simplePagination?: SimplePagination;
  image?: Image;
  gallery?: Image[];
  taxonomy?: Taxonomy;
}
export interface RootObject {
  success: number;
  results: Result[];
  pagination: Pagination;
}

export const transformPostsResponse = (
  data: RootObject, queryApi, options
) => {

  const results: any = data?.results

  const relatedPosts = results?.relatedPosts?.results 
    ? shapeRelatedPostsData(results.relatedPosts.results) 
    : null;

  const blocks: Block[] = results?.blocks 
    ? shapeBlockData(results.blocks) 
    : results?.blocks;

  const shapedResults = results?.map(item => {
    return {
      ...item,
      blocks,
      relatedPosts,
      title: item?.title ? decode(item.title) : null,
      description: item?.description ? decode(item.description) : null,
      authors: item?.authors
        ? item.authors.map((m) => {
          return {
            ID: m.ID,
            displayName: m.displayName,
            nicename: m.nicename,
            email: m.email,
            website: m.website,
            status: m.status,
            roles: m.roles,
          }
        }) : [],
    }
  });

  return {
    results: shapedResults,
    pagination: data.pagination,
  };
};

export const transformPostByIdResponse = (
  data: RootObject, queryApi, extraOptions
) => {
  const [item] = data.results
    ?.map(item => {

      return {
        ...item,
        title: item?.title ? decode(item.title) : null,
        description: item?.description ? decode(item.description) : null,

        date: item?.date
          ? moment(item.date)
            .format("MMMM Do, YYYY")
          : null,

        focalPoint: item?.image?.focalPoint
          ? item.image.focalPoint
          : null,

        authors: item?.authors?.map((m) => {
          return {
            ID: m.ID,
            displayName: m.displayName,
            avatar: m.avatar,
            nicename: m.nicename,
            email: m.email,
            website: m.website,
            status: m.status,
            roles: m.roles,
          }
        }) || [],
      }
    });

  return item;
};

const postApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPosts: build.query({
      query: (params) => {
        return {
          url: `/covr/v1/posts/?${queryString
            .stringify(params, {
              arrayFormat: 'comma'
            })}`,
          method: "GET",
        };
      },
      providesTags: ['Posts'],
      transformResponse: transformPostsResponse,
    }),
    getPostsSearch: build.query({
      query: (params) => {
        return {
          url: `/covr/v1/posts/search/?${queryString
            .stringify(params, {
              arrayFormat: 'comma'
            })}`,
          method: "GET",
        };
      },
      providesTags: ['Posts'],
      transformResponse: transformPostsResponse,
    }),
    getPostById: build.query({
      query: (params) => {
        return {
          url: `/covr/v1/posts/?${queryString.stringify(params)}`,
          method: "GET",
        };
      },
      providesTags: ['Posts'],
      transformResponse: transformPostByIdResponse,
    }),
  })
});

export const {
  useGetPostsQuery,
  useGetPostsSearchQuery,
  useGetPostByIdQuery,
} = postApi;
